import React, {FC, useMemo} from "react";
import Grid from "@mui/material/Grid";
import {format, parseISO} from "date-fns";
import ArrowRightAlt from "@mui/icons-material/ArrowRightAlt";
import Paper from "@mui/material/Paper";
import {LiveabaordPackage, ResortPackage, DiveShopPackage} from "../../types/package";
import {Typography, TypographyProps, Box, Tooltip} from "@mui/material";
import {useTranslation} from "next-i18next";
import {useForex} from "../../hooks/use-forex";
import NextLink from "next-translate-routes/link";
import Button from "@mui/material/Button";
import {useSelector} from "../../store";

const shortMonthFormatter = new Intl.DateTimeFormat(undefined, {
    month: "short"
});

const DateBox: FC<{ dateString: string }> = ({dateString}) => {
    const date = useMemo(() => {
        return parseISO(dateString)
    }, [dateString])
    return <Box sx={{display: "inline-block"}}>
        <Typography
            style={{
                fontSize: "2rem",
                lineHeight: "2.1rem",
                textAlign: "center"
            }}
        >
            {format(date, " d ")}
        </Typography>
        <Typography fontWeight={400}
                    style={{
                        fontSize: "1.2rem",
                        lineHeight: "1.2rem",
                        textTransform: "uppercase",
                        textAlign: "center"
                    }}
        >
            {shortMonthFormatter.format(date)}
        </Typography>
    </Box>
}

const NumNightsBox: FC<{ numNights: string }> = ({numNights}) => {
    return <Box sx={{display: "inline-block", pt: "4px"}}>
        <Typography display={"block"}
                    color={"text.secondary"}
                    fontSize={"smaller"}
        >
            {numNights}
        </Typography>
        <ArrowRightAlt sx={{width: 32, height: 32, color: "text.secondary"}}/>
    </Box>
}
export const LiveaboardRow: FC<{ pack: LiveabaordPackage }> = ({pack}) => {

    const {t} = useTranslation();
    const {convertCurrency} = useForex();
    const {selectedTripId} = useSelector((state) => state.userSession);

    let buttonText
    if (pack.availability === "FULL") {
        buttonText = t("full")
    } else if (pack.spotsRemaining > 0) {
        buttonText = t("spots-left", {count: pack.spotsRemaining})
    } else {
        buttonText = t("book-now-button")
    }

    return <>
        <Paper sx={{
            my: 2,
            mx: 0,
            py: {
                xs: 2,
                sm: 1
            },
            px: 2,
        }}>
            <Grid container alignItems='center'>
                <Grid item xs={4} sm={4} md={1} textAlign={"right"}>
                    <DateBox dateString={pack.tripStartDate}/>
                </Grid>
                <Grid item xs={4} sm={4} md={2} textAlign={"center"}>
                    <NumNightsBox numNights={`${pack.numNights} ${t("nights")}`}/>
                </Grid>
                <Grid item xs={4} sm={4} md={1} textAlign={"left"}>
                    <DateBox dateString={pack.tripEndDate}/>
                </Grid>
                <Grid item md={4} sm={12} xs={12} textAlign={"center"}>
                    <Typography
                        sx={{
                            fontSize: "1.2rem",
                            lineHeight: "1.2rem",
                            textTransform: "lowercase",
                            display: "block",
                            color: "text.secondary",
                            marginX: "4px",
                            mt: {
                                xs: 2,
                                sm: 1,
                                md: 0
                            },
                        }}
                    >
                        {t("starting-from")}
                    </Typography>

                        <Typography
                            style={{
                                fontSize: "1.8rem",
                            }}
                            sx={{
                                fontWeight: "bold",
                                color: "primary.dark",
                                lineHeight: "2.6rem",
                            }}
                        >
                            {convertCurrency(pack.startingPrice, pack.currency)}
                        </Typography>
                    {pack.discountPercent !== null &&
                        <Typography
                            style={{
                                fontSize: "0.7rem",
                                backgroundColor: "#e47303",
                                display: "inline-block",
                                position: "absolute",
                                borderRadius: "4px",
                                transform: "translate(-10%, -60%)"

                            }}
                            sx={{
                                mt: "4px",
                                py: "2px",
                                px: "4px",
                                fontWeight: "bold",
                                color: "white",
                                lineHeight: "0.8rem",
                            }}
                        >
                            {t("discount", {percent: Math.round(pack.discountPercent * 100)})}
                        </Typography>}

                </Grid>
                <Grid
                    item
                    zeroMinWidth
                    xs={12}
                    sm={12}
                    md={4}
                    textAlign={"center"}
                >
                    <NextLink href={{
                        pathname: "/request-booking",
                        query: {
                            tripIdEnc: window.btoa(selectedTripId)
                        }
                    }}
                              passHref
                              legacyBehavior
                    >
                        <Button
                            sx={{
                                mt: {
                                    xs: 2,
                                    sm: 1,
                                    md: 0
                                }
                            }}
                            component={"a"}
                            variant='contained'
                            color='secondary'
                            disabled={pack.availability === "FULL"}
                        >
                            {buttonText}
                        </Button>
                    </NextLink>
                </Grid>
            </Grid>
        </Paper>
    </>
}

export const ResortRow: FC<{ pack: ResortPackage }> = ({pack}) => {

    const {t} = useTranslation();
    const {convertCurrency} = useForex();
    const {selectedTripId} = useSelector((state) => state.userSession);

    return <>
        <Paper sx={{
            my: 2,
            mx: 0,
            py: {
                xs: 2,
                sm: 1
            },
            px: 2,
        }}>
            <Grid container alignItems='center'>
                <Grid item xs={12} sm={12} md={2} textAlign={"center"}>
                    <Box sx={{display: "inline-block"}}>
                        <Typography
                            style={{
                                fontSize: "2rem",
                                lineHeight: "2.1rem",
                                textAlign: "center"
                            }}
                        >
                            {pack.nights}
                        </Typography>
                        <Typography fontWeight={400}
                                    style={{
                                        fontSize: "1.2rem",
                                        lineHeight: "1.2rem",
                                        textAlign: "center"
                                    }}
                        >
                            {t("nights")}
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3} textAlign={"center"}>
                    <Typography
                        sx={{
                            mt: {
                                xs: 2,
                                sm: 1,
                                md: 0
                            },
                        }}
                        style={{
                            color: "black",
                            fontSize: "1.1rem",
                            lineHeight: "1.1rem",
                            fontWeight: 700
                        }}
                    >
                        {t("diver-label")}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: "1.2rem",
                            lineHeight: "1.2rem",
                            textTransform: "lowercase",
                            display: "block",
                            color: "text.secondary",
                            marginX: "4px",

                        }}
                    >
                        {t("starting-from")}
                    </Typography>

                    <Typography
                        style={{
                            fontSize: "1.8rem",
                        }}
                        sx={{
                            fontWeight: "bold",
                            color: "primary.dark",
                            lineHeight: "2.6rem",
                        }}
                    >
                        {convertCurrency(pack.startingPriceDiver, pack.currency)}
                    </Typography>
                    {pack.discountPercent !== null &&
                        <Typography
                            style={{
                                fontSize: "0.7rem",
                                backgroundColor: "#e47303",
                                display: "inline-block",
                                position: "absolute",
                                borderRadius: "4px",
                                transform: "translate(-10%, -60%)"

                            }}
                            sx={{
                                mt: "4px",
                                py: "2px",
                                px: "4px",
                                fontWeight: "bold",
                                color: "white",
                                lineHeight: "0.8rem",
                            }}
                        >
                            {t("discount", {percent: Math.round(pack.discountPercent * 100)})}
                        </Typography>}
                </Grid>
                <Grid item xs={12} sm={6} md={3} textAlign={"center"}>
                    <Typography
                        sx={{
                            mt: {
                                xs: 2,
                                sm: 1,
                                md: 0
                            },
                        }}
                        style={{
                            color: "black",
                            fontSize: "1.1rem",
                            lineHeight: "1.1rem",
                            fontWeight: 700
                        }}
                    >
                        {t("non-diver-label")}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: "1.2rem",
                            lineHeight: "1.2rem",
                            textTransform: "lowercase",
                            display: "block",
                            color: "text.secondary",
                            marginX: "4px",

                        }}
                    >
                        {t("starting-from")}
                    </Typography>

                    <Typography
                        style={{
                            fontSize: "1.8rem",
                        }}
                        sx={{
                            mb: {
                                xs: 2,
                                sm: 1,
                                md: 0
                            },
                            fontWeight: "bold",
                            color: "primary.dark",
                            lineHeight: "2.6rem",
                        }}
                    >
                        {convertCurrency(pack.startingPriceNonDiver, pack.currency)}
                    </Typography>
                    {pack.discountPercent !== null &&
                        <Typography
                            style={{
                                fontSize: "0.7rem",
                                backgroundColor: "#e47303",
                                display: "inline-block",
                                position: "absolute",
                                borderRadius: "4px",
                                transform: "translate(-10%, -60%)"

                            }}
                            sx={{
                                mt: "4px",
                                py: "2px",
                                px: "4px",
                                fontWeight: "bold",
                                color: "white",
                                lineHeight: "0.8rem",
                            }}
                        >
                            {t("discount", {percent: Math.round(pack.discountPercent * 100)})}
                        </Typography>}
                </Grid>
                <Grid item xs={12} sm={12} md={4} zeroMinWidth textAlign={"center"}>
                    <NextLink href={{
                        pathname: "/request-booking",
                        query: {
                            tripIdEnc: window.btoa(selectedTripId)
                        }
                    }}
                              passHref
                              legacyBehavior
                    >
                        <Button
                            sx={{
                                mt: {
                                    xs: 2,
                                    sm: 1,
                                    md: 0
                                }
                            }}
                            component={"a"}
                            variant='contained'
                            color='secondary'
                            disabled={pack.availability === "FULL"}
                        >
                            {t("book-now-button")}
                        </Button>
                    </NextLink>
                </Grid>
            </Grid>
        </Paper>
    </>
}

export const DiveShopRow: FC<{ pack: DiveShopPackage }> = ({pack}) => {

    const {t, i18n} = useTranslation();
    const {convertCurrency} = useForex();
    const {selectedTripId} = useSelector((state) => state.userSession);

    return <>
        <Paper sx={{
            my: 2,
            mx: 0,
            py: {
                xs: 2,
                sm: 1
            },
            px: 2,
        }}>
            <Grid container alignItems='center'>
                <Grid item xs={12} sm={12} md={5}
                      style={{flexGrow: 1, textAlign: "center"}}
                >
                    <Box sx={{display: "inline-block"}}>
                        <Typography
                            color={"text.secondary"}
                            style={{
                                fontSize: "2rem",
                                lineHeight: "2.1rem",
                                textAlign: "center"
                            }}
                        >
                            {eval("pack.offering_" + i18n.language)}
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} sm={12} md={3} textAlign={"center"}>

                    <Typography
                        sx={{
                            fontSize: "1.2rem",
                            lineHeight: "1.2rem",
                            textTransform: "lowercase",
                            display: "block",
                            color: "text.secondary",
                            marginX: "4px",
                            mt: {
                                xs: 2,
                                sm: 2,
                                md: 0
                            },
                        }}
                    >
                        {t("starting-from")}
                    </Typography>

                    <Typography
                        style={{
                            fontSize: "1.8rem",
                        }}
                        sx={{
                            mb: {
                                xs: 2,
                                sm: 2,
                                md: 0
                            },
                            display: "block",
                            fontWeight: "bold",
                            color: "primary.dark",
                            lineHeight: "2.6rem",
                            textAlign: "center"

                        }}
                    >
                        {convertCurrency(pack.price, pack.currency)}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={12} md={4} zeroMinWidth textAlign={"center"}>
                    <NextLink href={{
                        pathname: "/request-booking",
                        query: {
                            tripIdEnc: window.btoa(selectedTripId)
                        }
                    }}
                              passHref
                              legacyBehavior
                    >
                        <Button
                            component={"a"}
                            variant='contained'
                            color='secondary'
                            disabled={pack.availability === "FULL"}
                        >
                            {t("book-now-button")}
                        </Button>
                    </NextLink>
                </Grid>
            </Grid>
        </Paper>
    </>
}
